/**
 * Which app store the reader's device installs from: 'ios', 'android',
 * or null when it cannot be told (desktop web).
 *
 * App Review rejects an iOS build whose screens mention Android or
 * Google Play (guideline 2.3.10), and the field and report shells load
 * our pages from app.trailsiq.com — so anything a signed-out screen
 * says about the stores has to ask this first. Show both only when the
 * answer is null.
 *
 * iPadOS WKWebView sends a desktop "Macintosh" UA by default, and an
 * App Review iPad is exactly where that matters, so a touch-capable
 * Mac is read as iOS. The shells' UA markers answer for a remote-origin
 * page where the Capacitor bridge was not injected; Android's shells
 * carry "Android" in the UA too, so the marker alone means iOS.
 */
export function storePlatform() {
    if (typeof window === 'undefined') return null;
    const ua = window.navigator?.userAgent || '';
    if (/Android/i.test(ua)) return 'android';
    try {
        const p = window.Capacitor?.getPlatform?.();
        if (p === 'ios' || p === 'android') return p;
    } catch { /* fall through */ }
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    if (/Macintosh/i.test(ua) && (window.navigator?.maxTouchPoints || 0) > 1) return 'ios';
    if (/TrailsIQ(Field|Report)Native/.test(ua)) return 'ios';
    return null;
}

// The report app's name and its two store pages, in ONE place: they
// were copied into four files, which is how a rename leaves one behind.
// A page offering a store asks storePlatform() which, per the rule
// above. The App Store resolves the listing by its id, so the slug in
// the URL is cosmetic and an older one keeps working.
export const REPORT_APP_NAME = 'TrailsIQ Go';
export const REPORT_APP_IOS_STORE_URL = 'https://apps.apple.com/us/app/trailsiq-go/id6766092028';
export const REPORT_APP_ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.trailsiq.reportapp';
export const REPORT_APP_ANDROID_PACKAGE = 'com.trailsiq.reportapp';

/**
 * An Android intent link that opens `httpsUrl` in the installed Report
 * app, or the Play Store page when it is not installed.
 *
 * A plain https link to the app's own App Link paths is not enough
 * from a page on app.trailsiq.com: Chrome keeps a same-site tap in the
 * browser rather than handing it to the app. Chrome only follows an
 * intent: link from a tap, never from a page load, so this belongs on
 * a click handler.
 */
export function reportAppIntentUrl(httpsUrl) {
    const u = new URL(httpsUrl);
    return `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;`
        + `package=${REPORT_APP_ANDROID_PACKAGE};`
        + `S.browser_fallback_url=${encodeURIComponent(REPORT_APP_ANDROID_STORE_URL)};end`;
}
